// client/src/pages/GroupCreate.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupForm from '../components/GroupForm';
import { apiFetch, refreshSessionUser } from '../lib/api';
import { isApprovedOrganiser } from '../lib/roles';

/**
 * /groups/new — Only accessible to approved organisers or superadmins.
 * - We refresh the session first to avoid stale approval state.
 * - If not logged in → /login
 * - If logged in but not approved → /groups/organiser-request
 */
export default function GroupCreate() {
  const navigate = useNavigate();

  // Paint fast with cached user, then refresh once.
  const cached = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  }, []);
  const [me, setMe] = useState(cached);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const fresh = await refreshSessionUser();
        if (!mounted) return;
        setMe(fresh || null);

        if (!fresh) {
          navigate('/login', { replace: true });
          return;
        }
        if (!isApprovedOrganiser(fresh)) {
          navigate('/groups/organiser-request', { replace: true });
          return;
        }
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  async function handleSubmit(formData) {
    const res = await apiFetch('/api/groups/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to create group');
    navigate('/groups', { replace: true });
  }

  // Show nothing while gating/redirecting to avoid flicker.
  if (checking || !me || !isApprovedOrganiser(me)) return null;

  return (
    <div className="main-content">
      <GroupForm
        initialData={{}}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/groups')}
      />
    </div>
  );
}
